/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Header - SuperAdmin Portal Header
 * ============================================================================
 *
 * Header component with theme toggle, language toggle and user actions.
 * Uses the extensible notification system for bell icon notifications.
 *
 * ============================================================================
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, LogOut, Menu, ChevronDown, HelpCircle, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useLayoutStore, useFavorites, useFavoritesDisplayStyle, useMobileModuleSwitchStyle, FAVORITES_MAX } from '@/stores/layoutStore';
import { useModuleNavigation } from '@/hooks/useModuleNavigation';
import FavoritesBar, { FavoritesPaletteButton } from '@/components/layouts/favorites/FavoritesBar';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { useLogout } from '@/hooks/api/useAuth';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { NotificationPanel } from '@/components/ui/NotificationPanel';
import { useNotifications } from '@/hooks/api/useNotifications';
import { HelpPanel } from '@/components/guides/HelpPanel';
import { useMyLocations } from '@/modules/venta/hooks/useMyLocations';
import { useMyRegister } from '@/modules/venta/hooks/useCashRegisters';

/**
 * The connected user's VENTA context inside the profile popover: the location(s)
 * they're attached to and the cash register (till) assigned to them, with its
 * open/closed state highlighted. Rendered only while the popover is mounted, so
 * the underlying queries fire on popover-open — not on every page — and stay
 * fail-soft (rows render only when data is present; 403s for non-VENTA users are
 * swallowed by React Query and simply yield nothing).
 */
function UserVentaContext() {
  const { t } = useTranslation('common');
  const { data: myLocations } = useMyLocations();
  const { data: myRegister } = useMyRegister();

  return (
    <>
      {myLocations && myLocations.length > 0 && (
        <div className="header-user-popover-row">
          <span className="header-user-popover-label">
            {myLocations.length > 1
              ? t('header.userMenu.locations', 'Locations')
              : t('header.userMenu.location', 'Location')}
          </span>
          <span className="header-user-popover-value">
            {myLocations.map((l) => l.name).join(', ')}
          </span>
        </div>
      )}
      {myRegister && (
        <div className="header-user-popover-row">
          <span className="header-user-popover-label">{t('header.userMenu.till', 'Till')}</span>
          <span className="header-user-popover-till">
            <span
              className="header-user-popover-till-name"
              title={`${myRegister.name} · ${myRegister.code}`}
            >
              {myRegister.name} · {myRegister.code}
            </span>
            <span className={`header-user-popover-till-badge ${myRegister.open ? 'open' : 'closed'}`}>
              {myRegister.open
                ? t('header.userMenu.tillOpen', 'Open')
                : t('header.userMenu.tillClosed', 'Closed')}
            </span>
          </span>
        </div>
      )}
    </>
  );
}

export function Header() {
  const { t } = useTranslation('common');
  const { t: tPlatform } = useTranslation('platform');
  const navigate = useNavigate();
  const location = useLocation();
  const { logout: storeLogout, user } = useAuthStore();
  const { toggleSidebar } = useUIStore();
  const logoutMutation = useLogout();
  const favorites = useFavorites();
  const favoritesDisplayStyle = useFavoritesDisplayStyle();
  const addFavorite = useLayoutStore((s) => s.addFavorite);
  const removeFavorite = useLayoutStore((s) => s.removeFavorite);
  const currentPath = location.pathname;
  const isCurrentFavorited = favorites.includes(currentPath);
  const isFavFull = favorites.length >= FAVORITES_MAX[favoritesDisplayStyle];
  const handleToggleFavorite = () => {
    if (isCurrentFavorited) {
      removeFavorite(currentPath);
    } else if (!isFavFull) {
      addFavorite(currentPath);
    }
  };
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [headerModuleDropdownOpen, setHeaderModuleDropdownOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const moduleSwitchStyle = useMobileModuleSwitchStyle();
  const { availableModules, activeModule, showSwitcher } = useModuleNavigation();
  const headerModuleRef = useRef<HTMLDivElement>(null);

  const showHeaderPill = showSwitcher && moduleSwitchStyle === 'header';

  // Close header module dropdown on outside click
  useEffect(() => {
    if (!headerModuleDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (headerModuleRef.current && !headerModuleRef.current.contains(e.target as Node)) {
        setHeaderModuleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [headerModuleDropdownOpen]);

  // Close user menu popover on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  // Fetch notifications from the new extensible notification system
  const { data: notificationData, isLoading: isNotificationsLoading } = useNotifications();

  // Get notification count from the summary
  const notificationCount = notificationData?.totalCount || 0;

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    logoutMutation.mutate(undefined, {
      onSettled: () => {
        setLogoutDialogOpen(false);
        storeLogout();
        navigate('/auth/login');
      },
    });
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={toggleSidebar}>
          <Menu className="header-icon" />
        </button>

        {/* Header pill module switcher — mobile only, style === 'header' */}
        {showHeaderPill && activeModule && (
          <div className="hdr-module-pill-wrap" ref={headerModuleRef}>
            <button
              className="hdr-module-pill"
              style={{ '--hdr-mod-color': activeModule.color } as React.CSSProperties}
              onClick={() => setHeaderModuleDropdownOpen(!headerModuleDropdownOpen)}
              type="button"
            >
              <activeModule.icon size={14} style={{ color: activeModule.color }} />
              <span>{activeModule.shortName}</span>
              <ChevronDown size={12} className={`hdr-pill-chevron${headerModuleDropdownOpen ? ' hdr-pill-chevron-open' : ''}`} />
            </button>

            {headerModuleDropdownOpen && (
              <div className="hdr-module-dropdown">
                {availableModules.map((mod) => {
                  const Icon = mod.icon;
                  const isActive = mod.id === activeModule.id;
                  return (
                    <button
                      key={mod.id}
                      className={`hdr-module-option${isActive ? ' hdr-module-option-active' : ''}`}
                      onClick={() => {
                        setHeaderModuleDropdownOpen(false);
                        navigate(mod.basePath);
                      }}
                      type="button"
                    >
                      <Icon size={16} style={{ color: mod.color }} />
                      <span>{t(mod.nameKey)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Center — favorites zone */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <FavoritesBar />
      </div>

      <div className="header-right">
        <LanguageToggle size="sm" />
        <ThemeToggle showSystemOption size="sm" />
        <FavoritesPaletteButton />

        {/* Mobile: star toggle for current page */}
        <button
          className={`header-btn hdr-fav-toggle${isCurrentFavorited ? ' hdr-fav-active' : ''}`}
          onClick={handleToggleFavorite}
          disabled={!isCurrentFavorited && isFavFull}
          title={isCurrentFavorited ? t('favorites.remove') : t('favorites.add')}
          type="button"
        >
          <Star size={16} fill={isCurrentFavorited ? 'currentColor' : 'none'} />
        </button>

        <button
          className="header-btn"
          onClick={() => setHelpOpen(true)}
          title={tPlatform('guides.title')}
        >
          <HelpCircle className="header-icon" />
        </button>

        <div className="notification-wrapper">
          <button
            className="header-btn notification-btn"
            onClick={() => setNotificationPanelOpen(!notificationPanelOpen)}
            aria-label={t('header.notifications', 'Notifications')}
          >
            <Bell className="header-icon" />
            {notificationCount > 0 && (
              <span className="notification-badge">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </button>
          <NotificationPanel
            isOpen={notificationPanelOpen}
            onClose={() => setNotificationPanelOpen(false)}
            attentionItems={notificationData?.attentionItems || []}
            activityItems={notificationData?.activityItems || []}
            totalCount={notificationCount}
            isLoading={isNotificationsLoading}
          />
        </div>

        <div className="header-user-menu" ref={userMenuRef}>
          <button
            className="header-user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            title={user?.email}
            type="button"
          >
            <span className="header-user-avatar">
              {(user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '')}
            </span>
          </button>
          {showUserMenu && (
            <div className="header-user-popover">
              <div className="header-user-popover-header">
                <div className="header-user-popover-avatar">
                  {(user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '')}
                </div>
                <div className="header-user-popover-info">
                  <div className="header-user-popover-name">{user?.firstName} {user?.lastName}</div>
                  <div className="header-user-popover-email">{user?.email}</div>
                </div>
              </div>
              <div className="header-user-popover-details">
                <div className="header-user-popover-row">
                  <span className="header-user-popover-label">{t('header.userMenu.role', 'Role')}</span>
                  <span className="header-user-popover-value">{user?.role === 'ORG_ADMIN' ? t('header.userMenu.administrator', 'Administrator') : user?.role}</span>
                </div>
                <div className="header-user-popover-row">
                  <span className="header-user-popover-label">{t('header.userMenu.organization', 'Organization')}</span>
                  <span className="header-user-popover-value">{user?.orgName}</span>
                </div>
                <div className="header-user-popover-row">
                  <span className="header-user-popover-label">{t('header.userMenu.orgId', 'Org ID')}</span>
                  <span className="header-user-popover-value header-user-popover-mono">{user?.orgId}</span>
                </div>
                <UserVentaContext />
              </div>
              <button
                className="header-user-popover-logout"
                onClick={() => { setShowUserMenu(false); setLogoutDialogOpen(true); }}
                disabled={logoutMutation.isPending}
                type="button"
              >
                <LogOut size={14} />
                {t('header.logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={logoutDialogOpen}
        variant="warning"
        title={t('header.logoutDialog.title')}
        message={t('header.logoutDialog.message')}
        details={user && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('header.logoutDialog.user')}:</span>
              <span className="font-medium">{user.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('header.logoutDialog.session')}:</span>
              <span className="font-medium">{t('header.logoutDialog.willTerminate')}</span>
            </div>
          </div>
        )}
        confirmText={t('header.logout')}
        isLoading={logoutMutation.isPending}
        onConfirm={handleLogoutConfirm}
        onCancel={() => setLogoutDialogOpen(false)}
      />

      <HelpPanel isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      <style>{headerStyles}</style>
    </header>
  );
}

const headerStyles = `
  .app-header {
    display: flex;
    align-items: center;
    height: 52px;
    padding: 0 20px;
    background: var(--color-bg-secondary);
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .header-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .header-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .header-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .header-icon {
    width: 16px;
    height: 16px;
  }

  .notification-wrapper {
    position: relative;
  }

  .notification-btn {
    position: relative;
    padding: 8px;
  }

  .notification-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 99px;
    background: #DC2626;
    color: white;
    font-size: 9px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .header-user-menu {
    position: relative;
  }

  .header-user-btn {
    display: flex; align-items: center; justify-content: center;
    background: transparent; border: none; cursor: pointer; padding: 4px;
    border-radius: var(--radius-full);
    transition: all 0.15s;
  }

  .header-user-btn:hover {
    background: var(--color-bg-tertiary);
  }

  .header-user-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-primary-hover) 100%);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 600; color: var(--color-text-on-brand);
    text-transform: uppercase;
  }

  .header-user-popover {
    position: absolute; top: calc(100% + 8px); right: 0;
    width: 280px; background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg); box-shadow: var(--shadow-lg);
    z-index: 200; overflow: hidden;
    animation: header-popover-in 0.15s ease;
  }

  @keyframes header-popover-in {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .header-user-popover-header {
    display: flex; align-items: center; gap: 12px;
    padding: 16px; border-bottom: 1px solid var(--color-border-subtle);
  }

  .header-user-popover-avatar {
    width: 40px; height: 40px; border-radius: 50%;
    background: linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-primary-hover) 100%);
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 600; color: var(--color-text-on-brand);
    flex-shrink: 0; text-transform: uppercase;
  }

  .header-user-popover-info { min-width: 0; }

  .header-user-popover-name {
    font-size: 0.875rem; font-weight: 600;
    color: var(--color-text-primary);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  .header-user-popover-email {
    font-size: 0.75rem; color: var(--color-text-secondary);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  .header-user-popover-details {
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .header-user-popover-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 4px 0;
  }

  .header-user-popover-label {
    font-size: 0.75rem; color: var(--color-text-muted);
  }

  .header-user-popover-value {
    font-size: 0.75rem; font-weight: 500; color: var(--color-text-primary);
    max-width: 60%; text-align: right; overflow-wrap: anywhere;
  }

  .header-user-popover-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.6875rem; color: var(--color-text-secondary); font-weight: 400;
  }

  .header-user-popover-till {
    display: flex; align-items: center; gap: 6px;
    min-width: 0; max-width: 62%; justify-content: flex-end;
  }
  .header-user-popover-till-name {
    font-size: 0.75rem; font-weight: 600; color: var(--color-text-primary);
    min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .header-user-popover-till-badge {
    flex: 0 0 auto;
    font-size: 0.625rem; font-weight: 700; padding: 1px 6px; border-radius: 999px;
    text-transform: uppercase; letter-spacing: 0.03em;
  }
  .header-user-popover-till-badge.open {
    background: color-mix(in srgb, var(--color-success) 18%, transparent);
    color: var(--color-success);
  }
  .header-user-popover-till-badge.closed {
    background: color-mix(in srgb, var(--color-text-muted) 18%, transparent);
    color: var(--color-text-muted);
  }

  .header-user-popover-logout {
    display: flex; align-items: center; gap: 8px;
    width: 100%; padding: 12px 16px;
    background: transparent; border: none;
    font-size: 0.8125rem; color: var(--color-error);
    cursor: pointer; transition: background 0.15s;
  }

  .header-user-popover-logout:hover {
    background: var(--color-error);
    color: #fff;
  }

  .header-user-popover-logout:disabled {
    opacity: 0.5; cursor: not-allowed;
  }

  .mobile-menu-btn {
    display: none;
    padding: 8px;
    border-radius: 6px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .mobile-menu-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  /* Header pill module switcher — hidden on desktop */
  .hdr-module-pill-wrap {
    display: none;
    position: relative;
  }

  .hdr-module-pill {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 16px;
    border: 1px solid var(--hdr-mod-color, var(--color-border));
    background: color-mix(in srgb, var(--hdr-mod-color, var(--color-brand)) 8%, transparent);
    color: var(--color-text-primary);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .hdr-module-pill:hover {
    background: color-mix(in srgb, var(--hdr-mod-color, var(--color-brand)) 15%, transparent);
  }

  .hdr-pill-chevron {
    transition: transform 200ms ease;
  }

  .hdr-pill-chevron-open {
    transform: rotate(180deg);
  }

  .hdr-module-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 180px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    padding: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 200;
  }

  .hdr-module-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s ease;
  }

  .hdr-module-option:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .hdr-module-option-active {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
    font-weight: 600;
  }

  @media (max-width: 768px) {
    .mobile-menu-btn {
      display: none;
    }

    .hdr-module-pill-wrap {
      display: block;
    }
  }

  /* ── Favorite toggle star (mobile-visible, desktop-hidden) ── */
  .hdr-fav-toggle {
    display: none;
    color: var(--color-text-muted);
    transition: color 0.2s, transform 0.2s;
  }
  .hdr-fav-toggle:hover:not(:disabled) {
    color: var(--color-warning);
    transform: scale(1.1);
  }
  .hdr-fav-toggle:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  .hdr-fav-active {
    color: var(--color-warning);
  }
  @media (max-width: 768px) {
    .hdr-fav-toggle {
      display: flex;
    }
  }
`;
